import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import {
    Activity,
    Bot,
    BookOpen,
    ChevronRight,
    Cpu,
    LayoutDashboard,
    LayoutList,
    LogOut,
    type LucideIcon,
    Mail,
    Menu,
    MessagesSquare,
    Network,
    ScrollText,
    Search,
    ShieldCheck,
    Sparkles,
    UploadCloud,
    Users,
    Wrench,
    X,
} from 'lucide-react'
import { useAuthStore } from '~/stores/auth-store'
import { KNOKA_LOGOUT_URL } from '~/api/auth'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { ThemeToggle } from '~/components/ThemeToggle'
import { useCommandPalette } from '~/lib/use-command-palette'
import { cn } from '~/lib/utils'

// KRDS 백오피스 셸: 상단 고정 헤더 + 좌측 LNB + 본문(브레드크럼 + 콘텐츠).
// IA는 서비스 우선(per-service): 운영 대시보드 + 서비스/거버넌스/콘텐츠/플랫폼 그룹.
type NavItem = { to: string; label: string; end: boolean; icon: LucideIcon }
type NavGroup = { label?: string; items: NavItem[] }

const NAV_GROUPS: NavGroup[] = [
    {
        items: [
            {
                to: '/',
                label: '운영 대시보드',
                end: true,
                icon: LayoutDashboard,
            },
        ],
    },
    {
        label: '서비스',
        items: [
            {
                to: '/consultation',
                label: '상담',
                end: false,
                icon: MessagesSquare,
            },
            {
                to: '/certification',
                label: '인증평가',
                end: false,
                icon: ShieldCheck,
            },
            { to: '/maintenance', label: '유지하기', end: false, icon: Wrench },
            {
                to: '/monitoring',
                label: '모니터링',
                end: false,
                icon: Activity,
            },
        ],
    },
    {
        label: '거버넌스',
        items: [
            {
                to: '/governance/catalog',
                label: '기능 카탈로그',
                end: false,
                icon: Network,
            },
            { to: '/governance/models', label: '모델', end: false, icon: Cpu },
            {
                to: '/governance/prompts',
                label: '프롬프트',
                end: false,
                icon: Sparkles,
            },
            {
                to: '/governance/agents',
                label: '에이전트 설정',
                end: false,
                icon: Bot,
            },
        ],
    },
    {
        label: '콘텐츠',
        items: [
            {
                to: '/content/catalog',
                label: '카탈로그',
                end: false,
                icon: LayoutList,
            },
            {
                to: '/content/knowledge',
                label: '지식베이스',
                end: false,
                icon: BookOpen,
            },
            {
                to: '/content/uploads',
                label: '업로드 관리',
                end: false,
                icon: UploadCloud,
            },
        ],
    },
    {
        label: '플랫폼',
        items: [
            { to: '/users', label: '사용자/조직', end: false, icon: Users },
            {
                to: '/settings/notifications',
                label: '알림·발송 설정',
                end: false,
                icon: Mail,
            },
            { to: '/audit', label: '감사로그', end: false, icon: ScrollText },
        ],
    },
]

// 브레드크럼 항목: 라벨 + (이동 대상이 있으면) to. 그룹 라벨은 인덱스 라우트가
// 없으므로 to 없이 span으로, 페이지 항목은 to로 Link 렌더된다.
type Crumb = { label: string; to?: string }

// 현재 경로 → 브레드크럼 항목들. NAV 정의를 재사용한다.
function resolveBreadcrumb(pathname: string): Crumb[] {
    for (const group of NAV_GROUPS) {
        for (const item of group.items) {
            const matched = item.end
                ? pathname === item.to
                : pathname === item.to || pathname.startsWith(`${item.to}/`)
            if (matched) {
                if (item.to === '/') return []
                const page: Crumb = { label: item.label, to: item.to }
                return group.label ? [{ label: group.label }, page] : [page]
            }
        }
    }
    return []
}

// LNB 본문(메뉴). 데스크톱 aside와 모바일 드로어가 공유한다.
// onNavigate: 모바일에서 항목 선택 시 드로어를 닫기 위한 콜백.
function NavMenu({ onNavigate }: { onNavigate?: () => void }) {
    return (
        <nav aria-label='주요 메뉴' className='flex flex-1 flex-col p-2'>
            {NAV_GROUPS.map((group, gi) => (
                <div
                    key={group.label ?? `group-${gi}`}
                    className={cn(
                        'flex flex-col gap-0.5',
                        // 그룹 사이 구분선(구획감) — 첫 그룹 제외.
                        gi > 0 && 'border-border mt-2 border-t pt-2'
                    )}>
                    {group.label ? (
                        <div className='admin-eyebrow px-2.5 pt-1 pb-1.5'>
                            {group.label}
                        </div>
                    ) : null}
                    {group.items.map((item) => {
                        const Icon = item.icon
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                onClick={onNavigate}
                                className={({ isActive }) =>
                                    cn(
                                        'focus-visible:ring-ring flex h-8 items-center gap-2.5 rounded-[var(--radius-sm)] px-2.5 text-[13px] transition-colors outline-none focus-visible:ring-2',
                                        isActive
                                            ? 'bg-accent text-accent-foreground font-medium'
                                            : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                                    )
                                }>
                                <Icon className='size-4 shrink-0' />
                                {item.label}
                            </NavLink>
                        )
                    })}
                </div>
            ))}
        </nav>
    )
}

export function AdminLayout() {
    const location = useLocation()
    const adminRole = useAuthStore((s) => s.adminRole)
    const openPalette = useCommandPalette((s) => s.setOpen)
    const [navOpen, setNavOpen] = useState(false)

    const crumbs = resolveBreadcrumb(location.pathname)

    // 라우트 변경 시 모바일 드로어 닫기(안전망: 항목 onClick 외 경로 변화 포함).
    useEffect(() => {
        setNavOpen(false)
    }, [location.pathname])

    // 로그아웃은 서버사이드 흐름(쿠키·세션 정리 + knoka end_session)이라 전체 페이지를 이동한다.
    const onLogout = () => {
        window.location.href = KNOKA_LOGOUT_URL
    }

    return (
        <div className='bg-background flex h-screen flex-col overflow-hidden'>
            {/* 건너뛰기 링크 — 포커스 시에만 노출, 본문으로 이동 */}
            <a href='#main' className='skip-link'>
                본문 바로가기
            </a>
            <header className='border-border cc-glass sticky top-0 z-30 flex h-12 shrink-0 items-center justify-between gap-3 border-b px-4'>
                <div className='flex items-center gap-2.5'>
                    <Button
                        variant='ghost'
                        size='icon'
                        className='lg:hidden'
                        aria-label='주요 메뉴 열기'
                        aria-expanded={navOpen}
                        aria-controls='lnb'
                        onClick={() => setNavOpen((v) => !v)}>
                        <Menu />
                    </Button>
                    <img
                        src='/admin/brand/logo-lockup-horizontal.svg'
                        alt='HACCP AI'
                        className='h-9 w-auto dark:hidden'
                    />
                    <img
                        src='/admin/brand/logo-white.svg'
                        alt='HACCP AI'
                        className='hidden h-9 w-auto dark:block'
                    />
                    <span className='text-muted-foreground border-border border-l pl-2 text-[12px]'>
                        관리자
                    </span>
                    {/* ⌘K 커맨드 팔레트 트리거 — 검색형 버튼 + 단축키 칩(미니멀) */}
                    <button
                        type='button'
                        onClick={() => openPalette(true)}
                        aria-label='검색 또는 이동 (Command/Ctrl+K)'
                        className='border-input bg-card text-muted-foreground hover:bg-secondary hover:text-foreground focus-visible:ring-ring/40 ml-1 hidden h-7 items-center gap-2 rounded-[var(--radius)] border pr-1.5 pl-2.5 text-[12px] transition-colors outline-none focus-visible:ring-2 sm:flex'>
                        <Search className='size-3.5' />
                        <span className='w-32 text-left'>검색 또는 이동…</span>
                        <kbd className='border-border bg-secondary text-muted-foreground rounded-[4px] border px-1 font-sans text-[10px] leading-4'>
                            ⌘K
                        </kbd>
                    </button>
                    {/* 좁은 화면: 아이콘만 */}
                    <Button
                        variant='ghost'
                        size='icon-sm'
                        className='sm:hidden'
                        aria-label='검색 또는 이동 (Command/Ctrl+K)'
                        onClick={() => openPalette(true)}>
                        <Search />
                    </Button>
                </div>
                <div className='flex items-center gap-2'>
                    {adminRole ? (
                        <Badge tone='primary'>{adminRole}</Badge>
                    ) : null}
                    <ThemeToggle />
                    <Button variant='ghost' size='sm' onClick={onLogout}>
                        <LogOut />
                        로그아웃
                    </Button>
                </div>
            </header>

            <div className='flex min-h-0 flex-1'>
                {/* 데스크톱(lg↑): 고정 LNB */}
                <aside className='border-border bg-card hidden w-56 shrink-0 flex-col overflow-y-auto border-r lg:flex'>
                    <NavMenu />
                </aside>

                {/* 모바일(<lg): 배경 + 드로어. 배경 클릭 시 닫힘 */}
                {navOpen ? (
                    <div className='fixed inset-0 z-40 lg:hidden'>
                        <button
                            type='button'
                            aria-label='메뉴 닫기'
                            className='bg-foreground/40 absolute inset-0'
                            onClick={() => setNavOpen(false)}
                        />
                        <aside
                            id='lnb'
                            className='border-border cc-glass absolute inset-y-0 left-0 flex w-56 flex-col overflow-y-auto border-r shadow-lg'>
                            <div className='flex h-12 shrink-0 items-center justify-end px-2'>
                                <Button
                                    variant='ghost'
                                    size='icon'
                                    aria-label='메뉴 닫기'
                                    onClick={() => setNavOpen(false)}>
                                    <X />
                                </Button>
                            </div>
                            <NavMenu onNavigate={() => setNavOpen(false)} />
                        </aside>
                    </div>
                ) : null}

                <div className='flex min-w-0 flex-1 flex-col'>
                    <main
                        id='main'
                        tabIndex={-1}
                        className='cc-grid bg-background flex-1 overflow-y-auto px-6 py-5 outline-none'>
                        {crumbs.length > 0 ? (
                            <nav className='mb-4' aria-label='현재 경로'>
                                <ol className='flex items-center gap-1 text-[12px]'>
                                    <li className='flex items-center gap-1'>
                                        <Link
                                            to='/'
                                            className='text-muted-foreground hover:text-foreground'>
                                            홈
                                        </Link>
                                    </li>
                                    {crumbs.map((crumb, i) => {
                                        const current = i === crumbs.length - 1
                                        return (
                                            <li
                                                key={`${crumb.label}-${i}`}
                                                className='flex items-center gap-1'>
                                                <ChevronRight
                                                    aria-hidden='true'
                                                    className='text-muted-foreground size-3'
                                                />
                                                {crumb.to && !current ? (
                                                    <Link
                                                        to={crumb.to}
                                                        className='text-muted-foreground hover:text-foreground'>
                                                        {crumb.label}
                                                    </Link>
                                                ) : (
                                                    <span
                                                        className={
                                                            current
                                                                ? 'text-foreground'
                                                                : 'text-muted-foreground'
                                                        }
                                                        aria-current={
                                                            current
                                                                ? 'page'
                                                                : undefined
                                                        }>
                                                        {crumb.label}
                                                    </span>
                                                )}
                                            </li>
                                        )
                                    })}
                                </ol>
                            </nav>
                        ) : null}
                        <Outlet />
                    </main>
                </div>
            </div>
        </div>
    )
}
